import {useAgencyInvitesQuery, useAgencyMembersQuery} from "@/api/agencyApi";
import {
  useCompanyInvitesQuery,
  useCompanyMembersQuery,
  useCompanyQuery,
  useRespondToPermissionUpgradeRequestMutation,
} from "@/api/companyApi";
import CompanyMembers from "@/components/MultiAccount/CompanyMembers";
import EnableMultiAccountCta from "@/components/MultiAccount/EnableMultiAccountCta";
import PermissionGuide from "@/components/MultiAccount/PermissionGuide";
import SkeletonLoader from "@/components/onboarding/SkeletonLoader";
import {useIsAdmin} from "@/hooks/useUserHooks";
import {useSettingsAgencyStore} from "@/store/agency";
import type {TSettingsScope} from "@/store/types";
import useUserStore from "@/store/user";
import {type FC} from "react";
import {useTranslation} from "react-i18next";

const SKELETON_ROW_COUNT = 3;

const UsersPane: FC<{scope: TSettingsScope}> = ({scope}) => {
  const {t} = useTranslation(["common", "multi_account", "settings"]);
  const isAdmin = useIsAdmin();
  const hasMultiAccountAccess = useUserStore(state => state.user.has_multi_account_users_access);

  const scopeAgency = useSettingsAgencyStore(state => state.agency);
  const isAgencyScope = scope.type === "agency";
  const agencyId = isAgencyScope ? scope.id : 0;
  const companyId = isAgencyScope ? 0 : scope.id;
  const canViewMembers = hasMultiAccountAccess || isAdmin;

  const {data: company} = useCompanyQuery({companyId});
  const isSubbrand = company?.company_access_level === "SUB_BRAND";

  const {data: agencyInvites, isLoading: isLoadingAgencyInvites} = useAgencyInvitesQuery({
    agencyId,
    queryOptions: {enabled: !!agencyId && canViewMembers, refetchOnMount: true},
  });
  const {data: agencyMembers, isLoading: isLoadingAgencyMembers} = useAgencyMembersQuery({
    agencyId,
    queryOptions: {enabled: !!agencyId && canViewMembers, refetchOnMount: true},
  });
  const {data: companyInvites, isLoading: isLoadingInvites} = useCompanyInvitesQuery({
    companyId,
    queryOptions: {enabled: !!companyId && canViewMembers, refetchOnMount: true},
  });
  const {data: companyMembers, isLoading: isLoadingMembers} = useCompanyMembersQuery({
    companyId,
    queryOptions: {enabled: !!companyId && canViewMembers, refetchOnMount: true},
  });
  const {mutate: upgradePermission} = useRespondToPermissionUpgradeRequestMutation({companyId});

  const invites = isAgencyScope
    ? (agencyInvites
        ?.filter(invite => invite.status === "PENDING")
        .map(invite => ({...invite, permission: "ADMIN" as const})) ?? [])
    : (companyInvites?.filter(invite => invite.status === "PENDING") ?? []);
  const isLoading = isAgencyScope
    ? isLoadingAgencyInvites || isLoadingAgencyMembers
    : isLoadingInvites || isLoadingMembers;
  const members = isAgencyScope
    ? (agencyMembers ?? [])
    : ((isSubbrand ? companyMembers?.filter(member => !member.access_via_agency) : companyMembers) ?? []);

  return (
    <div className="flex flex-col gap-8">
      <span className="text-neutral-black text-lg leading-none font-bold">{t("common:users")}</span>
      {isLoading ? (
        <div className="flex flex-col gap-5">
          {Array.from({length: SKELETON_ROW_COUNT}).map((_, index) => (
            <div className="flex items-center justify-between gap-2" key={index}>
              <div className="border-neutral-grey-light-dark flex items-center gap-2 rounded-full border p-1 pr-2">
                <SkeletonLoader className="size-6 shrink-0 rounded-full" />
                <SkeletonLoader className="h-4 w-32" />
              </div>
              <SkeletonLoader className="h-8 w-28 shrink-0 rounded-lg" />
            </div>
          ))}
        </div>
      ) : !canViewMembers ? (
        <EnableMultiAccountCta />
      ) : (
        <>
          <CompanyMembers
            className="gap-4"
            companyId={isAgencyScope ? agencyId : companyId}
            companyName={(isAgencyScope ? scopeAgency.name : company?.company_name) ?? ""}
            companyInvites={invites}
            companyMembers={members}
            hideInviteHeadings
            isAgency={isAgencyScope}
            isLargeBrand={scopeAgency.large_brand}
            upgradePermission={
              isAgencyScope ? () => void 0 : memberId => upgradePermission({user_id: memberId, status: "REJECTED"})
            }
          />
          {isSubbrand && (
            <div className="flex flex-col gap-4">
              <span className="text-neutral-black text-lg leading-none font-bold">{t("multi_account:admins")}</span>
              <CompanyMembers
                className="gap-4"
                companyId={companyId}
                companyName={company?.company_name ?? ""}
                companyInvites={[]}
                companyMembers={companyMembers?.filter(member => member.access_via_agency) ?? []}
                isSubbrand
                upgradePermission={memberId => upgradePermission({user_id: memberId, status: "REJECTED"})}
              />
            </div>
          )}
          <div className="flex flex-col gap-4">
            <span className="text-neutral-black text-lg leading-none font-bold">{t("settings:permissions")}</span>
            <PermissionGuide />
          </div>
        </>
      )}
    </div>
  );
};

export default UsersPane;
